import { createAsyncThunk } from '@reduxjs/toolkit'
import { callErrorMsg } from 'helpers/errorMsg'
import { getExperimentTypeV2, getTargetChannel } from 'utils/apiList/abtesting'
import {
  getUserExperimentList,
  patchManualAssignments,
} from 'features/ABAdmin/services/ABAdminUserList'
import type {
  GetUserExperimentListParamsType,
  UnwhitelistUserPayloadType,
} from 'features/ABAdmin/@types/UserList'
import { toastSuccess } from 'utils/toast'
import {
  SLICE_NAME_AB_ADMIN_USER_LIST,
  setChannelOptions,
  setIsLoading,
  setList,
  setTypeOptions,
} from './slice'

const validateParamsValue = (key: keyof GetUserExperimentListParamsType, value: string) =>
  value ? { [key]: value } : {}

const mapToOptions = (values: string[]) => values.map((value) => ({ name: value, value }))

export const fetchUserExperimentList = createAsyncThunk(
  `${SLICE_NAME_AB_ADMIN_USER_LIST}/fetchUserExperimentList`,
  async (_, { dispatch, getState, rejectWithValue }) => {
    const {
      abAdminUserList: { query },
    } = getState() as StoreStateType

    // the list is only meaningful for a single user, so skip fetching until a user id is typed
    if (!query.user_id) {
      dispatch(setList([]))
      return []
    }

    dispatch(setIsLoading(true))

    try {
      const { data } = await getUserExperimentList({
        user_id: Number(query.user_id),
        ...validateParamsValue('target_channel', query.target_channel.value),
        ...validateParamsValue('experiment_type', query.type.value),
        ...validateParamsValue('assignment_status', query.assignment_status.value),
      })

      const experiments = data?.data?.experiments || []

      dispatch(setList(experiments))
      dispatch(setIsLoading(false))

      return experiments
    } catch (error) {
      callErrorMsg(error)
      dispatch(setIsLoading(false))
      return rejectWithValue(error)
    }
  },
)

export const fetchUserListFilterOptions = createAsyncThunk(
  `${SLICE_NAME_AB_ADMIN_USER_LIST}/fetchUserListFilterOptions`,
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const { data } = await getTargetChannel()

      dispatch(setChannelOptions(mapToOptions(data?.data?.target_channels || [])))

      return true
    } catch (error) {
      callErrorMsg(error, 'Gagal mendapatkan daftar filter')
      return rejectWithValue(error)
    }
  },
)

// the type list belongs to a channel, so it can only be fetched once one is picked
export const fetchUserListTypeOptions = createAsyncThunk(
  `${SLICE_NAME_AB_ADMIN_USER_LIST}/fetchUserListTypeOptions`,
  async (targetChannel: string, { dispatch, rejectWithValue }) => {
    if (!targetChannel) {
      dispatch(setTypeOptions([]))
      return []
    }

    try {
      const { data } = await getExperimentTypeV2({ target_channel: targetChannel })

      const typeOptions = mapToOptions(data?.data?.types || [])

      dispatch(setTypeOptions(typeOptions))

      return typeOptions
    } catch (error) {
      callErrorMsg(error, 'Gagal mendapatkan daftar tipe eksperimen')
      return rejectWithValue(error)
    }
  },
)

export const unwhitelistUser = createAsyncThunk(
  `${SLICE_NAME_AB_ADMIN_USER_LIST}/unwhitelistUser`,
  async ({ experimentId, userId, variantId }: UnwhitelistUserPayloadType, { rejectWithValue }) => {
    try {
      // unwhitelist reuses the manual assignment endpoint, a soft delete of the manual record
      const { data } = await patchManualAssignments({
        experimentId,
        users: [{ user_id: userId, variant_id: variantId, is_deleted: true }],
      })

      toastSuccess('User berhasil dihapus dari whitelist')

      return data
    } catch (error) {
      callErrorMsg(error)
      return rejectWithValue(error)
    }
  },
)
